import React, { useState } from "react";
import {
  IconButton,
  Typography,
  Box,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  RestoreFromTrash as RestoreIcon,
  DeleteForever as DeleteForeverIcon,
  MoreVert as MoreVertIcon,
} from "@mui/icons-material";
import { AlertDialog } from "../../../../../components/Dialog/AlertDialog";
import { api } from "../../../../../Assets/api/api";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { addStorageForItem } from "../../../../../utils/storageUsage";

const RecycleBinItemActions = ({ item, user, onActionComplete }) => {
  const { t } = useTranslation();
  const translation = t("fileExplorer");
  const [loading, setLoading] = useState({
    isRestoring: false,
    isDeleting: false,
    isDeleteDialogOpen: false,
    isRestoreDialogOpen: false,
  });

  // Don't render actions if user is not available
  if (!user || !user.slug) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography variant="caption" color="text.secondary">
          User not available
        </Typography>
      </Box>
    );
  }

  const restoreItem = async () => {
    try {
      setLoading((prev) => ({ ...prev, isRestoring: true }));
      const response = await api.put(
        `/api/account-storage/recycle-bin/restore/${item.id}${user?.slug}`
      );

      if (response?.data?.success) {
        toast.success(response?.data?.message || "Item restored successfully");
        if (onActionComplete) {
          onActionComplete();
        }
      } else {
        toast.error(response?.data?.message || "Failed to restore item");
      }
    } catch (error) {
      console.error("Error restoring item:", error);
      toast.error(error?.response?.data?.message || "Error restoring item");
    } finally {
      setLoading((prev) => ({
        ...prev,
        isRestoring: false,
        isRestoreDialogOpen: false,
      }));
    }
  };

  const deleteItem = async () => {
    try {
      setLoading((prev) => ({ ...prev, isDeleting: true }));
      const response = await api.delete(
        `/api/account-storage/recycle-bin/delete/${item.id}${user?.slug}`
      );

      if (response?.data?.success) {
        toast.success(response?.data?.message || "Item permanently deleted");
        // Add back storage for this item
        await addStorageForItem({ item, user, slug: user?.slug });
        if (typeof onActionComplete === "function") {
          onActionComplete();
        }
      } else {
        toast.error(response?.data?.message || "Failed to delete item");
      }
    } catch (error) {
      console.error("Error deleting item:", error);
      toast.error(error?.response?.data?.message || "Error deleting item");
    } finally {
      setLoading((prev) => ({
        ...prev,
        isDeleting: false,
        isDeleteDialogOpen: false,
      }));
    }
  };

  const restoreIcon = (
    <svg
      height="48"
      viewBox="0 0 48 48"
      width="48"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M0 0h48v48h-48z" fill="none" />
      <path d="M25.99 6c-9.95 0-17.99 8.06-17.99 18h-6l7.79 7.79.14.29 8.07-8.08h-6c0-7.73 6.27-14 14-14s14 6.27 14 14-6.27 14-14 14c-3.87 0-7.36-1.58-9.89-4.11l-2.83 2.83c3.25 3.26 7.74 5.28 12.71 5.28 9.95 0 18.01-8.06 18.01-18s-8.06-18-18.01-18zm-1.99 10v10l8.56 5.08 1.44-2.43-7-4.15v-8.5h-3z" />
    </svg>
  );

  const [menuAnchorEl, setMenuAnchorEl] = useState(null);

  const handleMenuOpen = (event) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleOpenRestoreDialog = () => {
    handleMenuClose();
    setLoading((prev) => ({ ...prev, isRestoreDialogOpen: true }));
  };

  const handleOpenDeleteDialog = () => {
    handleMenuClose();
    setLoading((prev) => ({ ...prev, isDeleteDialogOpen: true }));
  };
  return (
    <>
      <Box sx={{ minWidth: 40 }}>
        <IconButton size="small" onClick={handleMenuOpen}>
          <MoreVertIcon fontSize="small" />
        </IconButton>
        <Menu
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={handleMenuClose}
          onClick={(e) => e.stopPropagation()}
        >
          <MenuItem onClick={handleOpenRestoreDialog}>
            <ListItemIcon>
              <RestoreIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary={translation.restore} />
          </MenuItem>

          <MenuItem
            onClick={handleOpenDeleteDialog}
            sx={{ color: "error.main" }}
          >
            <ListItemIcon>
              <DeleteForeverIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText primary={translation.deletePermanently} />
          </MenuItem>
        </Menu>
      </Box>
      <AlertDialog
        open={loading.isDeleteDialogOpen}
        loading={loading.isDeleting}
        handleClose={() =>
          !loading.isDeleting &&
          setLoading((prev) => ({ ...prev, isDeleteDialogOpen: false }))
        }
        handleOk={() => {
          if (!loading.isDeleting) deleteItem();
        }}
        warningMessage={translation.confirmDeleteItem}
        topMessage={translation.deleteItemPermanently}
        undoMessage={translation.actionCannotBeUndone}
      />
      <AlertDialog
        open={loading.isRestoreDialogOpen}
        loading={loading.isRestoring}
        handleClose={() =>
          !loading.isRestoring &&
          setLoading((prev) => ({ ...prev, isRestoreDialogOpen: false }))
        }
        handleOk={() => {
          if (!loading.isRestoring) restoreItem();
        }}
        icon={restoreIcon}
        confirmColor="#1976d2"
        warningMessage={translation.confirmRestoreItem}
        topMessage={translation.restoreItem}
        undoMessage={translation.actionCannotBeUndone}
      />
    </>
  );
};

export default RecycleBinItemActions;
